/**
 * Catalogue of stats that can be surfaced on a player card in the
 * Players page. Each entry knows how to read its own value (so derived
 * stats like "appearances = starts + sub_appearances" Just Work), what
 * to label it, and a colour swatch for the card's number.
 *
 * Single source of truth — used by both the Players page renderer and
 * the Settings → Player Card Stats picker.
 */
export const PLAYER_STAT_FIELDS = [
  { key: 'goals',            label: 'Goals',            color: 'text-emerald-600', getValue: (p) => p?.stats?.goals || 0 },
  { key: 'assists',          label: 'Assists',          color: 'text-blue-600',    getValue: (p) => p?.stats?.assists || 0 },
  { key: 'starts',           label: 'Starts',           color: 'text-gray-700',    getValue: (p) => p?.stats?.starts || 0 },
  { key: 'player_of_match',  label: 'POTM',             color: 'text-amber-600',   getValue: (p) => p?.stats?.player_of_match || 0 },
  { key: 'appearances',      label: 'Appearances',      color: 'text-indigo-600',  getValue: (p) => (p?.stats?.starts || 0) + (p?.stats?.sub_appearances || 0) },
  { key: 'sub_appearances',  label: 'Sub Apps',         color: 'text-cyan-600',    getValue: (p) => p?.stats?.sub_appearances || 0 },
  { key: 'minutes_played',   label: 'Minutes',          color: 'text-rose-600',    getValue: (p) => p?.stats?.minutes_played || 0 },
  { key: 'yellow_cards',     label: 'Yellows',          color: 'text-yellow-500',  getValue: (p) => p?.stats?.yellow_cards || 0 },
  { key: 'red_cards',        label: 'Reds',             color: 'text-red-600',     getValue: (p) => p?.stats?.red_cards || 0 },
  { key: 'subbed_off',       label: 'Subbed Off',       color: 'text-gray-500',    getValue: (p) => p?.stats?.subbed_off || 0 },
  { key: 'unused_sub',       label: 'Unused Sub',       color: 'text-gray-400',    getValue: (p) => p?.stats?.unused_sub || 0 },
  { key: 'squad_selections', label: 'Squad Selections', color: 'text-purple-600',  getValue: (p) => p?.stats?.squad_selections || 0 },
  { key: 'own_goals',        label: 'Own Goals',        color: 'text-orange-600',  getValue: (p) => p?.stats?.own_goals || 0 },
];

/**
 * Default stats shown on a player card when the coach hasn't customised yet.
 * Matches the pre-CMS hard-coded layout: Goals · Assists · Starts · POTM.
 */
export const DEFAULT_PLAYER_CARD_STATS = ['goals', 'assists', 'starts', 'player_of_match'];

/** Hard cap on how many stat boxes can fit on one card. */
export const MAX_PLAYER_CARD_STATS = 10;

/**
 * Resolve a sanitised, capped list of stat field configs for the cards.
 * Falls back to DEFAULT_PLAYER_CARD_STATS when the user hasn't set any.
 */
export const resolvePlayerCardStats = (userSelection) => {
  const keys = (Array.isArray(userSelection) && userSelection.length > 0)
    ? userSelection
    : DEFAULT_PLAYER_CARD_STATS;
  const lookup = Object.fromEntries(PLAYER_STAT_FIELDS.map((f) => [f.key, f]));
  return keys
    .map((k) => lookup[k])
    .filter(Boolean)
    .slice(0, MAX_PLAYER_CARD_STATS);
};
